/**
 * In-app forgot-password screen (FR-2.11) — enter the username; the hook asks Cognito
 * to email a reset code and advances to the reset screen. Recovery needs the account's
 * verified email (D-43.2); accounts created under the old email-free pool cannot
 * self-recover until they add one (D-43.7).
 */

import { useState } from 'react';
import { validateUsername } from './validation';

export interface ForgotPasswordFormProps {
  busy: boolean;
  error: string | null;
  notice: string | null;
  onSubmit: (username: string) => void;
  onBack: () => void;
}

export function ForgotPasswordForm({
  busy,
  error,
  notice,
  onSubmit,
  onBack,
}: ForgotPasswordFormProps): React.JSX.Element {
  const [username, setUsername] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    const invalid = validateUsername(username);
    if (invalid) {
      setLocalError(invalid);
      return;
    }
    setLocalError(null);
    onSubmit(username.trim());
  };

  const shownError = localError ?? error;

  return (
    <form className="auth-form" data-testid="auth-forgot" onSubmit={submit} noValidate>
      <h2 className="auth-form__title">Reset your password</h2>
      <p className="muted">
        Enter your username and we will email a reset code to the address on your account.
      </p>
      {notice && (
        <div className="auth-form__notice" data-testid="auth-notice">
          {notice}
        </div>
      )}
      <input
        aria-label="Username"
        data-testid="auth-username"
        placeholder="Username"
        autoComplete="username"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        autoFocus
      />
      <button
        type="submit"
        className="btn btn--primary"
        data-testid="auth-submit"
        disabled={busy}
      >
        {busy ? 'Sending…' : 'Send reset code'}
      </button>
      {shownError && (
        <div className="error-text" data-testid="auth-error">
          {shownError}
        </div>
      )}
      <div className="auth-form__links">
        <button
          type="button"
          className="btn btn--link"
          data-testid="auth-goto-signin"
          onClick={onBack}
        >
          Back to sign in
        </button>
      </div>
    </form>
  );
}
